/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { AuthFetchTrustedServicesProviderProps } from './types';
import {
  enableAuthFetchTrustedServices,
  setJWTAccessTokenForTrustedServices,
} from '../../utils/authFetch';

const AuthFetchTrustedServicesContext = React.createContext<string>('');

/**
 * Provider that keeps authFetch's module-level trusted-services store in
 * sync with the current auth token and GW_AUTH_TRUSTED_HOSTS config.
 *
 * authFetch() is also called from outside the React tree (e.g. WMImage.ts,
 * the timeseries/soundings api clients), so the token itself cannot live in
 * React context alone -- this provider is what bridges React state into
 * that module-level store. It also exposes the token via context for React
 * consumers, see useAuthFetchToken.
 */
export const AuthFetchTrustedServicesProvider: React.FC<
  AuthFetchTrustedServicesProviderProps
> = ({ auth, authConfig, children }) => {
  // This will make the JWT token available for authFetch.
  React.useEffect(() => {
    setJWTAccessTokenForTrustedServices(auth?.token || '');
  }, [auth?.token]);

  // This will tell authFetch to do calls with the JWT token for specific trusted services.
  React.useEffect(() => {
    const trustedHosts = (authConfig?.GW_AUTH_TRUSTED_HOSTS ?? '')
      .split(',')
      .map((host) => host.trim())
      .filter((host) => host.length > 0);
    if (trustedHosts.length === 0) {
      return undefined;
    }
    enableAuthFetchTrustedServices(trustedHosts);
    return (): void => {
      enableAuthFetchTrustedServices(trustedHosts, false);
    };
  }, [authConfig?.GW_AUTH_TRUSTED_HOSTS]);

  return (
    <AuthFetchTrustedServicesContext.Provider value={auth?.token || ''}>
      {children}
    </AuthFetchTrustedServicesContext.Provider>
  );
};

/**
 * Consumer hook for React components that want to read the access token
 * currently registered with authFetch for trusted services. Must be used
 * within an AuthFetchTrustedServicesProvider; returns '' otherwise.
 */
export const useAuthFetchToken = (): string =>
  React.useContext(AuthFetchTrustedServicesContext);
